<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field class="is-flex is-align-items-center mb-2">
            <p class="control">
              <span class="mr-2">
                {{ $t('attributes.meeting_type') + ':' }}
              </span>
            </p>
            <b-checkbox
              v-model="filterType"
              v-for="option in typeOptions"
              type="is-info"
              :key="option.value"
              :native-value="option.value"
              @input="onFilter"
            >
              {{ option.name }}
            </b-checkbox>
          </b-field>

          <b-field class="mb-2">
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>

          <b-field class="mb-2">
            <b-datepicker
              :placeholder="
                $t('message.please_select') + $t('attributes.date_range')
              "
              v-model="selectedDates"
              range
              nearby-selectable-month-days
              style="width: 220px"
              @input="onSelectedDates"
            >
              <div class="buttons">
                <b-button
                  :label="$t('attributes.this_month')"
                  type="is-success"
                  size="is-small"
                  @click="onSelectThisMonth"
                />
                <b-button
                  :label="$t('attributes.next_month')"
                  type="is-info"
                  size="is-small"
                  @click="onSelectNextMonth"
                />
                <b-button
                  :label="$t('attributes.three_month')"
                  type="is-warning"
                  size="is-small"
                  @click="onSelectThreeMonth"
                />
              </div>
            </b-datepicker>
          </b-field>

          <b-field class="mb-2">
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.meeting') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      ref="table"
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      class="meeting-table"
      :row-class="rowClass"
      hoverable
      scrollable
      narrowed
      detailed
      detail-key="id"
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="page"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :visible="false"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.meeting_apply')"
        width="150"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-button
            v-if="!isApply(props.row)"
            size="is-small"
            rounded
            expanded
            @click.prevent.stop="onApply(props.row)"
            :disabled="!canApply(props.row)"
          >
            {{
              props.row.type === meeting_types.BRIEFING
                ? $t('attributes.meeting_apply_list')
                : $t('attributes.meeting_apply')
            }}
          </b-button>
          <b-button
            v-if="isApply(props.row)"
            type="is-warning"
            size="is-small"
            rounded
            :expanded="props.row.type === meeting_types.BRIEFING"
            @click.prevent.stop="onApplyModify(props.row)"
            :disabled="!canApplyModify(props.row)"
          >
            {{
              props.row.type === meeting_types.BRIEFING
                ? $t('attributes.meeting_apply_modify')
                : $t('attributes.modify')
            }}
          </b-button>
          <b-button
            v-if="isApply(props.row) && props.row.type === meeting_types.LESSON"
            type="is-danger"
            size="is-small"
            rounded
            @click.prevent.stop="onApplyCancel(props.row)"
            :disabled="!canApplyModify(props.row)"
          >
            {{ $t('attributes.cancel') }}
          </b-button>
        </div>
      </b-table-column>

      <b-table-column
        field="signup_deadline"
        :label="$t('attributes.signup_deadline')"
        width="110"
        sortable
        v-slot="props"
      >
        <p
          class="nowrap"
          :class="{
            'has-text-weight-semibold': props.row.is_expired,
            'has-text-danger': props.row.is_expired
          }"
        >
          {{ formatDate(props.row.signup_deadline) }}
        </p>
      </b-table-column>

      <b-table-column
        field="quantity"
        :label="$t('attributes.meeting_quantity')"
        width="90"
        sortable
        numeric
        v-slot="props"
      >
        <p
          class="nowrap"
          :class="{
            'has-text-weight-semibold':
              props.row.quantity >= props.row.capacity,
            'has-text-danger': props.row.quantity > props.row.capacity,
            'has-text-success': props.row.quantity === props.row.capacity
          }"
        >
          {{ props.row.quantity }} / {{ props.row.capacity }}
        </p>
      </b-table-column>

      <b-table-column
        field="type"
        :label="$t('attributes.meeting_type')"
        width="90"
        sortable
        v-slot="props"
      >
        <p class="nowrap">
          {{ $t(`attributes.meeting_type_${props.row.type}`) }}
        </p>
      </b-table-column>

      <b-table-column
        field="title"
        :label="$t('attributes.name')"
        width="180"
        sortable
        v-slot="props"
      >
        <p @click="toggleDetail(props.row)" style="cursor: pointer;">
          {{ props.row.title }}
        </p>
      </b-table-column>

      <b-table-column
        field="start_time"
        :label="$t('attributes.meeting_datetime')"
        width="150"
        sortable
        v-slot="props"
      >
        <p class="nowrap">{{ formatDateWeek(props.row.start_time) }}</p>
        <p class="nowrap">
          {{ formatTimes(props.row.start_time, props.row.end_time) }}
        </p>
      </b-table-column>

      <b-table-column
        field="group_code"
        :label="$t('attributes.travel_group')"
        sortable
        v-slot="props"
      >
        <a :href="groupUrl(props.row)" target="_blank" class="nowrap">
          {{ props.row.group_code }}
        </a>
        <p style="font-size: 12px;" v-html="props.row.group_name"></p>
      </b-table-column>

      <b-table-column
        field="leader"
        :label="$t('attributes.leader')"
        width="70"
        sortable
        v-slot="props"
      >
        <p class="nowrap">{{ props.row.leader }}</p>
      </b-table-column>

      <b-table-column
        field="place"
        :label="$t('attributes.meeting_place')"
        width="70"
        sortable
        v-slot="props"
      >
        <p class="nowrap">{{ props.row.place }}</p>
      </b-table-column>

      <b-table-column
        field="lecturer"
        :label="$t('attributes.lecturer')"
        width="70"
        sortable
        v-slot="props"
      >
        <p class="nowrap">{{ props.row.lecturer }}</p>
      </b-table-column>

      <b-table-column
        field="material"
        :label="$t('attributes.material')"
        width="90"
        sortable
        v-slot="props"
      >
        <p class="nowrap">{{ props.row.material }}</p>
        <span v-if="props.row.material_file">
          <a
            :href="fantasyFileUrl(props.row.material_file)"
            target="_blank"
            class="nowrap"
          >
            ({{ $t('attributes.download') }})
          </a>
          <b-button
            type="is-ghost"
            icon-left="content-copy"
            style="font-size: 0.5rem"
            @click="onCopyDownloadUrl(props.row)"
          ></b-button>
        </span>
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="120"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-button
            v-if="canModify(props.row)"
            type="is-primary"
            @click.prevent="onModify(props.row)"
          >
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #detail="props">
        <meeting-lesson
          v-if="props.row.type === meeting_types.LESSON"
          :meeting="props.row"
          @onSend="onModifyApplies"
        />
        <meeting-briefing
          v-else-if="props.row.type === meeting_types.BRIEFING"
          :meeting="props.row"
          :sending="sending"
          @onSend="onModifyApplies"
        />
      </template>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <meeting-form
        :meeting="selectedItem"
        :start-hour="startHour"
        :end-hour="endHour"
        :sending="sending"
        @onCheckTime="onCheckTime"
        @onSend="onSend"
        @onDelete="onDelete"
      />
    </b-modal>

    <b-modal
      :active.sync="isLessonApplyFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <meeting-lesson-apply-form
        :isOwner="false"
        :meeting="selectedItem"
        :meeting-apply="meetingApply"
        :sending="sending"
        @onCreate="onCreateApplies"
        @onUpdate="onUpdateApplies"
        @onDelete="onDeleteApply"
      />
    </b-modal>

    <b-modal
      :active.sync="isBriefingApplyFormModalActive"
      :can-cancel="['x']"
      :width="1200"
      has-modal-card
    >
      <meeting-briefing-apply-form
        :meeting="selectedItem"
        :meeting-applies="meetingApplies"
        :sending="sending"
        @onSend="onSendApplies"
      />
    </b-modal>
  </section>
</template>

<script>
import Vue from 'vue'
import debounce from 'lodash/debounce'
import { MEETING_PLACE_TYPE, MEETING_TYPE } from '@/utils/constants'
import { AREA_API, MEETING_API, MEETING_ROOMS_API } from '@/utils/apis'
import MeetingLesson from './MeetingLesson'
import MeetingBriefing from './MeetingBriefing'
import MeetingForm from './MeetingForm'
import MeetingLessonApplyForm from './MeetingLessonApplyForm'
import MeetingBriefingApplyForm from './MeetingBriefingApplyForm'
import notifyService from '../../services/notifyService'

export default {
  name: 'MeetingList',
  components: {
    MeetingLesson,
    MeetingBriefing,
    MeetingForm,
    MeetingLessonApplyForm,
    MeetingBriefingApplyForm
  },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    const { startDate, endDate } = this.calculateDateRange(this.query)

    const filterType = this.query.type
      ? this.query.type.split(',')
      : ['briefing', 'lesson']

    return {
      startHour: 7,
      endHour: 22,

      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      filterType,
      typeOptions: [
        {
          value: 'briefing',
          name: Vue.i18n.translate(`attributes.meeting_type_briefing`)
        },
        {
          value: 'lesson',
          name: Vue.i18n.translate(`attributes.meeting_type_lesson`)
        }
      ],

      search: this.query.search || '',
      sortField: this.query.sort || 'start_time',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'desc',
      page: parseInt(this.query.page) || 1,
      perPage: parseInt(this.query.page_size) || 10,

      loadingAreas: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,

      selectedDates: [startDate, endDate],
      startDate,
      endDate,

      meeting_types: MEETING_TYPE,

      isFormModalActive: false,

      meetingApply: null,
      isLessonApplyFormModalActive: false,

      meetingApplies: null,
      isBriefingApplyFormModalActive: false,

      sending: false
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems || this.loadingAreas
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
        }

        await this.loadItems()
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedAreaCode = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadItems() {
      try {
        const api = `${MEETING_API}`
        const params = {
          area_code: this.selectedAreaCode,
          type: this.filterType.join(','),
          search: this.search,
          start_date: this.startDate,
          end_date: this.endDate,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items.map(m => ({
          ...m,
          applicants: m.applicants
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .map((a, index) => ({
              ...a,
              index: index + 1
            }))
        }))
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('meeting', e)
      } finally {
        this.loadingItems = false
      }
    },
    toggleDetail(row) {
      this.$refs.table.toggleDetails(row)
    },
    getQuery(query) {
      this.filterType = query.type ? query.type.split(',') : []
      this.search = query.search || ''
      this.sortField = query.sort || 'start_time'
      this.sortOrder = query.order || 'desc'
      this.page = parseInt(query.page) || 1
      this.perPage = parseInt(query.page_size) || 10

      this.selectedArea = null
      this.selectedAreaCode = query.ac || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
      }

      const { startDate, endDate } = this.calculateDateRange(query)
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDates = [startDate, endDate]
    },
    updateQuery() {
      const query = {
        ac: this.selectedAreaCode,
        type: this.filterType.join(','),
        search: this.search,
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix(),
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null

      this.updateQuery()
    },
    calculateDateRange(query) {
      let startDate = null
      let endDate = null

      if (query.start && query.end) {
        startDate = this.$dayjs.unix(query.start).toDate()
        endDate = this.$dayjs.unix(query.end).toDate()
      } else {
        const today = this.$dayjs()
        startDate = today.startOf('month').toDate()
        endDate = today
          .add(2, 'month')
          .endOf('month')
          .toDate()
      }

      return { startDate, endDate }
    },
    onSelectedDates() {
      this.page = 1
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.updateQuery()
    },
    onSelectThisMonth() {
      const today = this.$dayjs()
      this.selectMonth(today)
    },
    onSelectNextMonth() {
      const day = this.$dayjs()
        .endOf('month')
        .add(1, 'day')
      this.selectMonth(day)
    },
    onSelectThreeMonth() {
      const today = this.$dayjs()
      this.startDate = today.startOf('month').toDate()
      this.endDate = today
        .add(2, 'month')
        .endOf('month')
        .toDate()
      this.selectedDates = [this.startDate, this.endDate]

      this.page = 1
      this.updateQuery()
    },
    selectMonth(day) {
      this.startDate = day.startOf('month').toDate()
      this.endDate = day.endOf('month').toDate()
      this.selectedDates = [this.startDate, this.endDate]

      this.page = 1
      this.updateQuery()
    },
    onClearDates() {
      this.page = 1
      this.selectedDates = null
      this.startDate = null
      this.endDate = null

      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },
    async onCopyDownloadUrl(item) {
      const url = this.fantasyFileUrl(item.material_file)

      await navigator.clipboard.writeText(url)

      return notifyService.success('檔案連結已複製到剪貼簿。')
    },

    canModify(item) {
      const user = Vue.$auth.user()

      if (user.is_admin) {
        return true
      }

      if (item.employee_code === user.emp_code) {
        return true
      }

      if (item.type === MEETING_TYPE.BRIEFING) {
        return user.area_code_perm.includes(item.area_code)
      }

      return false
    },

    canApply(item) {
      return !item.is_expired // && !item.is_full
    },
    canApplyModify(item) {
      if (item.is_expired) {
        return false
      }

      return true
    },
    isApply(item) {
      if (!Vue.$auth.check() || !Vue.$auth.user()) {
        return false
      }

      const user = Vue.$auth.user()

      if (item.type === MEETING_TYPE.LESSON) {
        const idx = item.applicants.findIndex(
          e => e.employee_code === user.emp_code
        )
        return idx !== -1
      } else if (item.type === MEETING_TYPE.BRIEFING) {
        const applicants = item.applicants.filter(
          e => e.employee_code === user.emp_code
        )

        return applicants.length > 0
      } else {
        return false
      }
    },
    onApply(item) {
      const user = Vue.$auth.user()

      if (item.type === MEETING_TYPE.LESSON) {
        this.selectedItem = item
        this.meetingApply = {
          id: null,
          employee_code: user.emp_code,
          employee_name: user.name,
          name: user.name,
          tel: null,
          remark: null
        }
        this.isLessonApplyFormModalActive = true
      } else if (item.type === MEETING_TYPE.BRIEFING) {
        this.selectedItem = item
        this.meetingApplies = {
          employee_code: user.emp_code,
          employee_name: user.name,
          applies: []
        }
        this.isBriefingApplyFormModalActive = true
      }
    },
    onApplyModify(item) {
      const user = Vue.$auth.user()
      if (item.type === MEETING_TYPE.LESSON) {
        const idx = item.applicants.findIndex(
          e => e.employee_code === user.emp_code
        )

        if (idx === -1) {
          return
        }

        this.selectedItem = item
        this.meetingApply = item.applicants[idx]
        this.isLessonApplyFormModalActive = true
      } else if (item.type === MEETING_TYPE.BRIEFING) {
        const applies = item.applicants.filter(
          e => e.employee_code === user.emp_code
        )

        this.selectedItem = item
        this.meetingApplies = {
          employee_code: user.emp_code,
          employee_name: user.name,
          applies: applies
        }
        this.isBriefingApplyFormModalActive = true
      }
    },
    onApplyCancel(item) {
      if (item.type !== MEETING_TYPE.LESSON) {
        return
      }

      const user = Vue.$auth.user()
      const idx = item.applicants.findIndex(
        e => e.employee_code === user.emp_code
      )

      if (idx === -1) {
        return
      }

      this.selectedItem = item
      this.onDeleteApply(item.applicants[idx])
    },
    onAdd() {
      const start_time = this.$dayjs()
        .startOf('day')
        .add(1, 'day')
        .hour(this.startHour)
        .minute(0)
      const end_time = start_time.add(1, 'hour')
      const signup_deadline = this.$dayjs().startOf('day')

      this.selectedItem = {
        id: null,
        title: null,
        type: MEETING_TYPE.LESSON,
        group_code: null,
        start_time: start_time.toDate(),
        end_time: end_time.toDate(),
        place_type: MEETING_PLACE_TYPE.INNER,
        place: null,
        schedule_id: null,
        schedule: null,
        capacity: 0,
        leader: null,
        lecturer: null,
        assistant: null,
        signup_deadline: signup_deadline.toDate(),
        meal_provided: false,
        material: null,
        material_file: null,
        tel: null,
        description: null
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    async onCheckTime(room_id, data) {
      const api = `${MEETING_ROOMS_API}/${room_id}/check-time`

      try {
        this.sending = true
        const result = await this.axios.post(api, data)
        const list = result.data.data

        if (list.length === 0) {
          return notifyService.success('所選時間未被預約')
        }

        const times = list.map(s => {
          const start_time = this.$dayjs(s.start_time).toDate()
          const end_time = this.$dayjs(s.end_time).toDate()
          return `${start_time.formatTime()}~${end_time.formatTime()}`
        })

        return notifyService.error(`以下時間已被預約：${times}`)
      } catch (e) {
        notifyService.error('', e)
      } finally {
        this.sending = false
      }
    },
    onSend(item) {
      if (this.selectedItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('message.confirm_cancel_meeting_', {
          name: item.title
        }),
        type: 'is-danger',
        confirmText: '確定取消',
        onConfirm: () => {
          this.deleteItem(item)
        }
      })
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(MEETING_API, item)

        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${MEETING_API}/${item.id}`

        this.sending = true
        await this.axios.put(api, item)

        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteItem(item) {
      const api = `${MEETING_API}/${item.id}`

      try {
        this.sending = true
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.meeting_canceled'))

        this.isFormModalActive = false
        await this.loadItems()
      } catch (e) {
        notifyService.error(this.$t('attributes.meeting_cancel_failed'), e)
      } finally {
        this.sending = false
      }
    },

    onCreateApplies(applies) {
      this.createApplies(applies)
    },
    onUpdateApplies(applicants) {
      this.updateApplies(this.selectedItem, applicants)
    },
    onDeleteApply(apply) {
      this.$buefy.dialog.confirm({
        message: this.$t('message.confirm_cancel_meeting'),
        type: 'is-danger',
        confirmText: '確定取消',
        onConfirm: () => {
          this.deleteApply(apply)
        }
      })
    },
    onModifyApplies(meeting, applies) {
      this.updateApplies(meeting, applies)
    },
    onSendApplies(applies) {
      this.updateApplies(this.selectedItem, applies)
    },
    async createApplies(applies) {
      try {
        const api = `${MEETING_API}/${this.selectedItem.id}/applicants`

        this.sending = true
        await this.axios.post(api, { applies })

        this.isBriefingApplyFormModalActive = false
        this.isLessonApplyFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateApplies(meeting, applicants) {
      try {
        const api = `${MEETING_API}/${meeting.id}/applicants`

        this.sending = true
        await this.axios.put(api, { applicants })

        this.isBriefingApplyFormModalActive = false
        this.isLessonApplyFormModalActive = false
        notifyService.success(this.$t('attributes.modify_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.modify_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteApply(apply) {
      const api = `${MEETING_API}/${this.selectedItem.id}/applicants/${apply.id}`

      try {
        this.sending = true
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.meeting_apply_canceled'))

        this.isBriefingApplyFormModalActive = false
        this.isLessonApplyFormModalActive = false
        await this.loadItems()
      } catch (e) {
        notifyService.error(
          this.$t('attributes.meeting_apply_cancel_failed'),
          e
        )
      } finally {
        this.sending = false
      }
    },

    rowClass(item) {
      if (item.is_expired) {
        return 'has-text-dark has-background-danger-light'
      }

      if (item.is_full) {
        return 'has-text-dark has-background-success-light'
      }

      return ''
    }
  }
}
</script>

<style lang="scss">
.meeting-table {
  margin-top: 0.75rem;

  .table {
    min-width: 1400px;
  }
}
</style>
